import { useState } from 'react';
import {
  Button,
  Chip,
  Icon,
  MetaGrid,
  MetaItem,
  Pill,
  Screen,
  ScreenHeader,
  SummaryTiles,
  StatTile,
} from '@garnet/ui';
import type { VerticalData } from '../data/types.js';

/* Garnet · Translation services.
   Shows the translation service provider (Bramham) as a CONNECTED sub-provider in the
   professional-services network, the language/service adapters it is connected for, and
   the live request → connector → provider → files-back loop that resolves a blocked
   document check. Translation is a professional service (PRD §29), not a machine data
   connector — so the provider plugs in here, alongside notary / trust-lawyer / assessor.
   This screen is a self-contained demo surface (no backend wiring). */

// ── the connected provider ──────────────────────────────────────────────────
const PROVIDER = {
  name: 'Bramham',
  kind: 'Certified translation service',
  residency: 'EU · in-region processing',
  sla: '2 days',
  endpoint: 'api.bramham.io',
  connected: true,
  // adapters = the specific languages & services Bramham is connected for
  languages: [
    { pair: 'ar↔en', live: true },
    { pair: 'ar↔fr', live: true },
    { pair: 'de↔en', live: true },
    { pair: 'fr↔en', live: true },
    { pair: 'es↔en', live: true },
    { pair: 'zh↔en', live: false },
  ],
  services: [
    { name: 'Sworn / certified human', live: true },
    { name: 'Notary-grade certified', live: true },
    { name: 'Machine-assist draft', live: false },
  ],
};

// ── the routing demo · request → connector → Bramham → files back → ensured ───
interface FlowStep {
  k: string;
  label: string;
  detail: string;
  /** which lane node this step activates */
  node: 0 | 1 | 2 | 3;
}
const FLOW: readonly FlowStep[] = [
  { k: 'raised', label: 'Request raised', detail: 'ar → en · REC-6610 · Arabic test report (8 pp)', node: 0 },
  { k: 'routed', label: 'Routed via Garnet connector', detail: 'secure handoff · ar↔en adapter · evidence opened', node: 1 },
  { k: 'received', label: 'Bramham received the request', detail: 'acknowledged · queued to a sworn translator', node: 2 },
  { k: 'translating', label: 'Translating', detail: 'sworn translator · certified service · SLA 2d', node: 2 },
  { k: 'returned', label: 'Files returned', detail: 'certified_translation.pdf · captured as hash-chained evidence', node: 3 },
  { k: 'ensured', label: 'Work ensured', detail: 'check d3 · certificate scope → verified (attestation · gold)', node: 3 },
];

const LANES = [
  { key: 'garnet', label: 'Garnet', sub: 'you raise the request', icon: 'file' as const },
  { key: 'conn', label: 'Connector', sub: 'language adapter routes it', icon: 'link' as const },
  { key: 'bramham', label: 'Bramham', sub: 'translates & certifies', icon: 'book' as const },
  { key: 'deliver', label: 'Deliverable', sub: 'certified files return', icon: 'download' as const },
];

export function TranslationServices(_props: { data: VerticalData }) {
  // step = 0 idle; 1..FLOW.length = completed through that step
  const [step, setStep] = useState(0);
  const reduce =
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function run() {
    setStep(0);
    let i = 0;
    const tick = () => {
      i += 1;
      setStep(i);
      if (i < FLOW.length) window.setTimeout(tick, reduce ? 0 : 850);
    };
    window.setTimeout(tick, reduce ? 0 : 350);
  }

  const done = step >= FLOW.length;
  const activeNode = step > 0 ? FLOW[Math.min(step, FLOW.length) - 1]!.node : -1;
  const liveLangs = PROVIDER.languages.filter((l) => l.live).length;

  return (
    <Screen>
      <ScreenHeader
        eyebrow="§ Translation services"
        title="Bramham — connected translation service"
        lead="Translation is a professional service in Garnet's in-country network. Bramham plugs in here as a connected provider with per-language and per-service adapters. A blocked document raises a request that routes through the connector to Bramham; the certified files come back as attested evidence and resolve the check."
        actions={
          <Button variant="primary" icon={done ? 'refresh' : 'send'} onClick={run}>
            {done ? 'Run again' : step > 0 ? 'Running…' : 'Send a translation request'}
          </Button>
        }
      />

      <SummaryTiles min={170}>
        <StatTile label="Provider" value="Bramham" sub="certified · EU residency" />
        <StatTile label="Language adapters" value={`${liveLangs}/${PROVIDER.languages.length}`} sub="live connections" />
        <StatTile label="SLA" value={PROVIDER.sla} sub="avg turnaround" />
        <StatTile label="Delivered · 30d" value={14} sub="certified translations" />
      </SummaryTiles>

      <div className="split-pane">
        {/* ── left · the live request routing loop ── */}
        <div className="panel pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>
              Request routing · live
            </div>
            {done && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: 'var(--verified-ink)' }}>
                <Icon name="check" size={13} /> work ensured
              </span>
            )}
          </div>

          {/* lane diagram */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, alignItems: 'stretch', marginBottom: 20 }}>
            {LANES.map((lane, i) => {
              const reached = activeNode >= i && step > 0;
              const isActive = activeNode === i && !done;
              const complete = activeNode > i || (done && i <= 3);
              const accent = complete ? 'var(--verified)' : isActive ? 'var(--ink)' : 'var(--rule)';
              const bg = complete ? 'var(--verified-bg)' : isActive ? 'var(--paper)' : 'var(--panel)';
              return (
                <div key={lane.key} style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: '1px solid ' + accent,
                      background: bg,
                      borderRadius: 'var(--radius-md)',
                      padding: '12px 12px',
                      textAlign: 'center',
                      transition: 'all .25s ease',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        border: '1px solid ' + accent,
                        color: complete ? '#fff' : isActive ? 'var(--ink)' : 'var(--muted)',
                        background: complete ? 'var(--verified)' : 'transparent',
                        marginBottom: 7,
                      }}
                    >
                      <Icon name={complete ? 'check' : lane.icon} size={15} />
                    </span>
                    <div style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 13.5, color: reached ? 'var(--ink)' : 'var(--muted)' }}>{lane.label}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', marginTop: 3, lineHeight: 1.35 }}>{lane.sub}</div>
                  </div>
                  {i < LANES.length - 1 && (
                    <span style={{ flex: '0 0 22px', textAlign: 'center', color: activeNode > i ? 'var(--verified)' : 'var(--rule)', transition: 'color .25s' }}>
                      <Icon name="arrow" size={16} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* stepped status log */}
          <div style={{ borderTop: '1px solid var(--rule)' }}>
            {FLOW.map((s, i) => {
              const state = step > i + 1 || done ? 'done' : step === i + 1 ? 'active' : 'idle';
              return (
                <div
                  key={s.k}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '22px 1fr',
                    gap: 11,
                    alignItems: 'start',
                    padding: '11px 0',
                    borderBottom: i < FLOW.length - 1 ? '1px solid var(--rule)' : 0,
                    opacity: state === 'idle' ? 0.42 : 1,
                    transition: 'opacity .25s',
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      marginTop: 1,
                      border: '1px solid ' + (state === 'done' ? 'var(--verified)' : state === 'active' ? 'var(--ink)' : 'var(--rule)'),
                      background: state === 'done' ? 'var(--verified)' : 'transparent',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                    }}
                  >
                    {state === 'done' ? <Icon name="check" size={11} /> : null}
                  </span>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)' }}>
                      {s.label}
                      {s.k === 'ensured' && state === 'done' && (
                        <span style={{ marginLeft: 8 }}>
                          <Pill tone="m">verified · gold</Pill>
                        </span>
                      )}
                    </div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 3, lineHeight: 1.45 }}>{s.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {step === 0 && (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 14 }}>
              A foreign-language document (DOC-9921 · Arabic test report) is blocking check d3. Press “Send a translation request” to route it to Bramham.
            </div>
          )}
        </div>

        {/* ── right · the connected provider + adapters ── */}
        <div className="panel pad">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--ink)',
                  color: 'var(--paper)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--display)',
                  fontWeight: 700,
                  fontSize: 17,
                  flex: 'none',
                }}
              >
                B
              </span>
              <div>
                <div style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 16 }}>{PROVIDER.name}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{PROVIDER.kind}</div>
              </div>
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: 'var(--verified-ink)', background: 'var(--verified-bg)', border: '1px solid var(--verified)', borderRadius: 'var(--radius-sm)', padding: '3px 8px' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--verified)' }} /> connected
            </span>
          </div>

          <div style={{ marginTop: 16 }}>
            <MetaGrid labelWidth={120}>
              <MetaItem label="Residency">{PROVIDER.residency}</MetaItem>
              <MetaItem label="SLA">{PROVIDER.sla}</MetaItem>
              <MetaItem label="Endpoint"><span className="mono">{PROVIDER.endpoint}</span></MetaItem>
              <MetaItem label="Act type">translator · professional service (PRD §29)</MetaItem>
            </MetaGrid>
          </div>

          {/* language adapters */}
          <div style={{ marginTop: 18 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 9 }}>
              Language adapters
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {PROVIDER.languages.map((l) => (
                <span
                  key={l.pair}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontFamily: 'var(--mono)',
                    fontSize: 11.5,
                    fontWeight: 600,
                    padding: '5px 9px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid ' + (l.live ? 'var(--rule)' : 'var(--rule)'),
                    background: l.live ? 'var(--panel)' : 'var(--rule-soft)',
                    color: l.live ? 'var(--ink)' : 'var(--muted)',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: l.live ? 'var(--verified)' : 'var(--muted-2)' }} />
                  {l.pair}
                </span>
              ))}
            </div>
          </div>

          {/* service adapters */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 9 }}>
              Service adapters
            </div>
            <div style={{ display: 'grid', gap: 7 }}>
              {PROVIDER.services.map((s) => (
                <div key={s.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                  <span>{s.name}</span>
                  {s.live ? <Pill tone="m">connected</Pill> : <Pill tone="n">available</Pill>}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--rule)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button sm variant="secondary" icon="plus">Add language / service</Button>
            <Button sm variant="ghost" icon="link">Manage connection</Button>
          </div>
        </div>
      </div>
    </Screen>
  );
}
